import { Group, Menu, Stack, Tooltip } from '@mantine/core';
import { IconAlertSquareRounded, IconArrowUpLeft, IconChevronDown, IconEye, IconSubtask } from '@tabler/icons-react';
import { RevisionGetResponse as Revision } from 'api/actions/revision-get/revision-get-response';
import { useApi } from 'api/api-context';
import DeviceStructureIcon from 'components/DeviceStructureIcon';
import DeviceFiles from 'components/forms/device/DeviceFiles';
import { DeviceFile } from 'components/forms/device/DeviceForm';
import { RevisionFormData } from 'components/forms/revision/fill-out/types';
import H3Medium from 'components/typography/H3Medium';
import panic from 'errors/panic';
import { DeviceFileType } from 'model/DeviceFileType';
import { ROLE_ADMIN_ID } from 'model/Role';
import RevisionDeviceMeasurementsModal from 'pages/revisions-module/revisions/RevisionDeviceMeasurementsModal';
import { IForm, IFormSection } from 'pages/revisions-module/template-editor/editors/form/types';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEVICE_FAULTS_PAGE_PATH, EDIT_DEVICE_PAGE_PATH } from 'routes/paths';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';

type Device = Revision['devices'][number];

interface RevisionDeviceProps {
  device: Device;
  kind: Revision['kind'];
  departmentId: number;
  deviceTypeId: number;
  canBrowseDevices: boolean;
  measurementSections: IFormSection[];
  revisionData: RevisionFormData | null;
  revisionHasParentDevice: boolean;
}

/**
 * A single device in a revision.
 */
function RevisionDevice({
  device,
  kind,
  departmentId,
  deviceTypeId,
  canBrowseDevices,
  measurementSections,
  revisionData,
  revisionHasParentDevice,
}: RevisionDeviceProps) {
  const { isTablet } = useResponsiveDesign();
  const [openedMeasurementSection, setOpenedMeasurementSection] = useState<IFormSection | null>(null);
  const deviceFiles = useMemo<DeviceFile[]>(
    () =>
      (device.files ?? []).map((file) => ({
        fileId: file.fileId,
        fileName: file.fileName,
        originalFileName: file.fileName,
        uploadedAt: new Date(file.created!),
        key: file.fileId,
        type: file.fileType as DeviceFileType,
        deleted: false,
        isMainImage: file.isMainImage,
        hasKepSignature: file.hasKepSignature,
      })),
    [device.files]
  );

  const detailLink = useMemo(() => EDIT_DEVICE_PAGE_PATH.insert({ deviceId: device.deviceId }), [device.deviceId]);
  const faultsLink = useMemo(() => DEVICE_FAULTS_PAGE_PATH.insert({ deviceId: device.deviceId }), [device.deviceId]);
  const showMeasurementsButton = useMemo(
    () => measurementSections.length > 0 && device.structure !== 'parent',
    [device.structure, measurementSections.length]
  );
  const measurementValuesBySection = useMemo(
    () => revisionData?.devices[String(device.deviceId)]?.measurements ?? {},
    [device.deviceId, revisionData]
  );
  const openedMeasurementSpec = useMemo(
    () =>
      openedMeasurementSection
        ? openedMeasurementSection.rows.flatMap(({ inputs }) => inputs.map(({ spec }) => spec))
        : [],
    [openedMeasurementSection]
  );
  const openedMeasurements = useMemo(
    () => (openedMeasurementSection ? measurementValuesBySection[openedMeasurementSection.id] ?? [] : []),
    [measurementValuesBySection, openedMeasurementSection]
  );

  return (
    <Stack spacing={24} py={24} px={isTablet ? 20 : 40} bg="white" style={{ borderRadius: '4px' }}>
      <Group
        position={isTablet ? 'left' : 'apart'}
        spacing={8}
        noWrap={!isTablet}
        align={isTablet ? 'stretch' : 'center'}
      >
        <Group spacing={8} noWrap={!isTablet} align="center">
          {device.structure === 'child' && revisionHasParentDevice && (
            <Tooltip label="Podradené zariadenie" openDelay={200} withinPortal>
              <IconArrowUpLeft size={16} stroke={1.8} />
            </Tooltip>
          )}
          <H3Medium color="gray.7">Zariadenie: {device.deviceName}</H3Medium>
          <DeviceStructureIcon structure={device.structure} />
        </Group>
        {(canBrowseDevices || showMeasurementsButton) && (
          <Group position="right" spacing={8} noWrap={!isTablet} w={isTablet ? '100%' : undefined}>
            {canBrowseDevices && (
              <>
                <Button
                  ml={isTablet ? 0 : 8}
                  component={Link}
                  to={detailLink}
                  size="sm"
                  variant="outline"
                  fullWidth={isTablet}
                  leftIcon={<IconEye />}
                >
                  Detail zariadenia
                </Button>
                <Button
                  component={Link}
                  to={faultsLink}
                  size="sm"
                  variant="outline"
                  fullWidth={isTablet}
                  leftIcon={<IconAlertSquareRounded />}
                >
                  Nedostatky zariadenia
                </Button>
              </>
            )}
            {showMeasurementsButton && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button
                    size="sm"
                    variant="outline"
                    fullWidth={isTablet}
                    leftIcon={<IconSubtask />}
                    rightIcon={<IconChevronDown />}
                  >
                    Merania
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {measurementSections.map((measurementSection) => (
                    <Menu.Item
                      key={measurementSection.id}
                      onClick={() => setOpenedMeasurementSection(measurementSection)}
                    >
                      {measurementSection.name}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        )}
      </Group>
      <DeviceFiles
        context="revision"
        deviceId={device.deviceId}
        deviceName={device.deviceName}
        departmentId={departmentId}
        files={deviceFiles}
        readonly
        persistActiveTab={false}
        initialTab={kind as any}
      />
      {openedMeasurementSection && (
        <RevisionDeviceMeasurementsModal
          deviceName={device.deviceName}
          deviceTypeId={deviceTypeId}
          measurementType={openedMeasurementSection.name}
          opened={!!openedMeasurementSection}
          onClose={() => setOpenedMeasurementSection(null)}
          spec={openedMeasurementSpec}
          lockingEnabled={openedMeasurementSection.measurementsAllowRowLocking ?? false}
          initialValues={{ measurements: openedMeasurements }}
          revisionKind={kind as 'revision' | 'form'}
        />
      )}
    </Stack>
  );
}

/**
 * Component for displaying devices in a revision.
 */
export default function RevisionDevices({ revision }: { revision: Revision }) {
  const { getAction, roleId } = useApi();
  const [canBrowseDevices, setCanBrowseDevices] = useState(roleId === ROLE_ADMIN_ID);
  const revisionForm = useMemo(
    () => (revision.revisionTemplate?.formScheme ? (JSON.parse(revision.revisionTemplate.formScheme) as IForm) : null),
    [revision.revisionTemplate?.formScheme]
  );
  const revisionData = useMemo(
    () => (revision.revisionData ? (JSON.parse(revision.revisionData) as RevisionFormData) : null),
    [revision.revisionData]
  );
  const measurementSections = useMemo(
    () => revisionForm?.sections.filter(({ tab }) => tab === 'measurements') ?? [],
    [revisionForm]
  );

  const parentDevice = useMemo(
    () => revision.devices.find((device) => device.structure === 'parent'),
    [revision.devices]
  );

  const otherDevices = useMemo(
    () => revision.devices.filter((device) => device.structure !== 'parent'),
    [revision.devices]
  );

  const sortedDevices = useMemo(
    () => (parentDevice ? [parentDevice, ...otherDevices] : otherDevices),
    [revision.devices]
  );

  useEffect(() => {
    const getPermissions = getAction('AuthUserGetPermissionsInDepartment');

    getPermissions({ parameters: { departmentId: String(revision.department.departmentId) } })
      .then((response) => setCanBrowseDevices(!!response.find(({ slug }) => slug === 'browse-devices')))
      .catch(panic);
  }, [revision.department.departmentId]);

  return (
    <Stack spacing={24}>
      {sortedDevices.map((device) => (
        <RevisionDevice
          key={device.deviceId}
          device={device}
          kind={revision.kind}
          departmentId={revision.department.departmentId}
          deviceTypeId={revision.deviceType.deviceTypeId}
          canBrowseDevices={canBrowseDevices}
          measurementSections={measurementSections}
          revisionData={revisionData}
          revisionHasParentDevice={!!parentDevice}
        />
      ))}
    </Stack>
  );
}
